import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash, ChevronDown, ChevronUp, Hotel, MapPin } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ImageUploader } from '@/components/ui/image-uploader';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ItineraryTagsTab } from '@/components/itinerary/ItineraryTagsTab';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Database } from '@/integrations/supabase/types';
import { RichTextEditor } from '@/components/ui/rich-text-editor/RichTextEditor';

type ContentStatus = Database['public']['Enums']['content_status'];
type Json = Database['public']['Tables']['itinerary_days']['Insert']['activities'];

interface Activity {
  title: string;
  description: string;
  affiliate_link?: string;
}

interface Accommodation {
  name: string;
  booking_link?: string;
  notes?: string;
}

interface ItineraryDay {
  id?: string;
  itinerary_id?: string;
  day_number: number;
  title: string;
  location?: string;
  description: string;
  activities?: Activity[];
  accommodation?: Accommodation;
  open?: boolean;
}

const parseActivities = (json: Json | null): Activity[] => {
  if (!json || !Array.isArray(json)) {
    return [];
  }
  
  return json.map(item => {
    if (typeof item !== 'object' || item === null) {
      return { title: '', description: '' };
    }
    
    const activity = item as Record<string, unknown>;
    return {
      title: typeof activity.title === 'string' ? activity.title : '',
      description: typeof activity.description === 'string' ? activity.description : '',
      affiliate_link: typeof activity.affiliate_link === 'string' ? activity.affiliate_link : undefined
    };
  });
};

const parseAccommodation = (json: Json | null): Accommodation | undefined => {
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return undefined;
  }
  
  const accommodation = json as Record<string, unknown>;
  
  if (typeof accommodation.name !== 'string') {
    return undefined;
  }
  
  return {
    name: accommodation.name,
    booking_link: typeof accommodation.booking_link === 'string' ? accommodation.booking_link : undefined,
    notes: typeof accommodation.notes === 'string' ? accommodation.notes : undefined
  };
};

const EditItinerary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState('');
  const [costEstimate, setCostEstimate] = useState('');
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [featuredImage, setFeaturedImage] = useState('');
  const [days, setDays] = useState<ItineraryDay[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('details');

  const { data: itinerary, isLoading, error } = useQuery({
    queryKey: ['itinerary', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('itineraries')
        .select(`
          *,
          itinerary_days(*)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Itinerary not found');
      
      if (data.itinerary_days) {
        data.itinerary_days.sort((a, b) => a.day_number - b.day_number);
      }
      
      return data;
    },
  });

  const { data: itineraryTags } = useQuery({
    queryKey: ['itineraryTags', id],
    queryFn: async () => {
      return [];
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (itinerary) {
      setTitle(itinerary.title || '');
      setDescription(itinerary.description || '');
      setDestination(itinerary.destination || '');
      setDuration(itinerary.duration?.toString() || '');
      setCostEstimate(itinerary.cost_estimate?.toString() || '');
      setStatus(itinerary.status || 'draft');
      setFeaturedImage(itinerary.featured_image || '');
      
      const formattedDays: ItineraryDay[] = itinerary.itinerary_days.map(day => {
        const parsedActivities = parseActivities(day.activities);
        const parsedAccommodation = parseAccommodation(day.accommodation);
        
        return {
          id: day.id,
          itinerary_id: day.itinerary_id,
          day_number: day.day_number,
          title: day.title || '',
          location: day.location || '',
          description: day.description || '',
          activities: parsedActivities,
          accommodation: parsedAccommodation,
          open: false,
        };
      });
      
      setDays(formattedDays);
    }
  }, [itinerary]);

  useEffect(() => {
    if (itineraryTags) {
      setSelectedTagIds([]);
    }
  }, [itineraryTags]);

  const updateItinerary = useMutation({
    mutationFn: async () => {
      const { data: updatedItinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .update({
          title,
          description,
          destination,
          duration: duration ? parseInt(duration) : null,
          cost_estimate: costEstimate ? parseFloat(costEstimate) : null,
          status,
          featured_image: featuredImage,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select();

      if (itineraryError) throw itineraryError;

      const { data: existingDays, error: daysError } = await supabase
        .from('itinerary_days')
        .select('id')
        .eq('itinerary_id', id);

      if (daysError) throw daysError;

      const existingDayIds = existingDays.map(d => d.id);
      const currentDayIds = days.filter(d => d.id).map(d => d.id as string);

      const daysToDelete = existingDayIds.filter(id => !currentDayIds.includes(id));

      const daysToUpdate = days.filter(d => d.id);

      const daysToInsert = days.filter(d => !d.id);

      if (daysToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('itinerary_days')
          .delete()
          .in('id', daysToDelete);

        if (deleteError) throw deleteError;
      }

      for (const day of daysToUpdate) {
        const { error: updateError } = await supabase
          .from('itinerary_days')
          .update({
            day_number: day.day_number,
            title: day.title,
            location: day.location,
            description: day.description,
            activities: day.activities as unknown as Json,
            accommodation: day.accommodation as unknown as Json,
            updated_at: new Date().toISOString(),
          })
          .eq('id', day.id);

        if (updateError) throw updateError;
      }

      if (daysToInsert.length > 0) {
        const daysForInsert = daysToInsert.map(day => ({
          itinerary_id: id,
          day_number: day.day_number,
          title: day.title,
          location: day.location,
          description: day.description,
          activities: day.activities as unknown as Json,
          accommodation: day.accommodation as unknown as Json,
        }));
        
        const { error: insertError } = await supabase
          .from('itinerary_days')
          .insert(daysForInsert);

        if (insertError) throw insertError;
      }
      
      console.log('Tags to save:', selectedTagIds);
      
      return true;
    },
    onSuccess: () => {
      toast.success('Itinerary updated successfully');
      navigate(`/dashboard/itineraries/${id}`);
    },
    onError: (error) => {
      console.error('Error updating itinerary:', error);
      toast.error(`Failed to update: ${error.message}`);
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title) {
      toast.error('Title is required');
      return;
    }

    setIsSubmitting(true);
    updateItinerary.mutate();
  };

  const addDay = () => {
    const dayNumber = days.length > 0 
      ? Math.max(...days.map(day => day.day_number)) + 1 
      : 1;
    
    setDays([...days, { 
      day_number: dayNumber,
      title: `Day ${dayNumber}`,
      location: '',
      description: '',
      activities: [],
      accommodation: { name: '', booking_link: '', notes: '' },
      itinerary_id: id,
      open: true
    }]);
  };

  const updateDay = (index, field, value) => {
    const updatedDays = [...days];
    updatedDays[index] = { ...updatedDays[index], [field]: value };
    setDays(updatedDays);
  };

  const removeDay = (index) => {
    const updatedDays = days.filter((_, i) => i !== index);
    const reorderedDays = updatedDays.map((day, i) => ({
      ...day,
      day_number: i + 1
    }));
    setDays(reorderedDays);
  };
  
  const toggleDayOpen = (index) => {
    const updatedDays = [...days];
    updatedDays[index] = { 
      ...updatedDays[index], 
      open: !updatedDays[index].open 
    };
    setDays(updatedDays);
  };
  
  const addActivity = (index) => {
    const updatedDays = [...days];
    const currentActivities = updatedDays[index].activities || [];
    updatedDays[index] = { 
      ...updatedDays[index], 
      activities: [...currentActivities, { title: '', description: '', affiliate_link: '' }]
    };
    setDays(updatedDays);
  };
  
  const updateActivity = (dayIndex, activityIndex, field, value) => {
    const updatedDays = [...days];
    if (!updatedDays[dayIndex].activities) {
      updatedDays[dayIndex].activities = [];
    }
    
    const updatedActivities = [...updatedDays[dayIndex].activities];
    updatedActivities[activityIndex] = { 
      ...updatedActivities[activityIndex], 
      [field]: value 
    };
    
    updatedDays[dayIndex] = { 
      ...updatedDays[dayIndex], 
      activities: updatedActivities 
    };
    setDays(updatedDays);
  };
  
  const removeActivity = (dayIndex, activityIndex) => {
    const updatedDays = [...days];
    const updatedActivities = updatedDays[dayIndex].activities.filter(
      (_, i) => i !== activityIndex
    );
    
    updatedDays[dayIndex] = { 
      ...updatedDays[dayIndex], 
      activities: updatedActivities 
    };
    setDays(updatedDays);
  };
  
  const updateAccommodation = (dayIndex, field, value) => {
    const updatedDays = [...days];
    const currentAccommodation = updatedDays[dayIndex].accommodation || { name: '', booking_link: '', notes: '' };
    
    updatedDays[dayIndex] = { 
      ...updatedDays[dayIndex], 
      accommodation: { ...currentAccommodation, [field]: value }
    };
    setDays(updatedDays);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="glass-card">
        <CardContent className="flex flex-col items-center justify-center h-64">
          <p className="text-destructive text-lg mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button onClick={() => navigate('/dashboard/itineraries')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Itineraries
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button variant="outline" onClick={() => navigate(`/dashboard/itineraries/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <h1 className="text-3xl font-bold tracking-tight ml-4">Edit Itinerary</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="days">Day-by-Day</TabsTrigger>
            <TabsTrigger value="tags">Tags</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Itinerary Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a title for your itinerary"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <RichTextEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Enter a description of your itinerary"
                    minHeight={200}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination</Label>
                    <Input
                      id="destination"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      placeholder="Where is this itinerary for?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (days)</Label>
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder="How many days?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="costEstimate">Cost Estimate ($)</Label>
                    <Input
                      id="costEstimate"
                      type="number"
                      min="0"
                      step="0.01"
                      value={costEstimate}
                      onChange={(e) => setCostEstimate(e.target.value)}
                      placeholder="Estimated cost"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select 
                      value={status} 
                      onValueChange={(value) => setStatus(value as ContentStatus)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <ImageUploader
                  onImageUploaded={setFeaturedImage}
                  currentImage={featuredImage}
                  label="Featured Image"
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="days">
            <Card className="glass-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Day-by-Day Itinerary</CardTitle>
                <Button type="button" variant="outline" onClick={addDay}>
                  <Plus className="h-4 w-4 mr-2" /> Add Day
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {days.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <p>No days added yet. Click "Add Day" to start building your itinerary.</p>
                  </div>
                ) : (
                  days.map((day, index) => (
                    <Collapsible 
                      key={index} 
                      open={day.open} 
                      onOpenChange={() => toggleDayOpen(index)}
                      className="border rounded-lg overflow-hidden"
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-4 cursor-pointer bg-muted/50">
                          <div className="flex items-center">
                            <span className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">
                              {day.day_number}
                            </span>
                            <span className="font-medium">
                              {day.title || `Day ${day.day_number}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeDay(index);
                              }}
                            >
                              <Trash className="h-4 w-4 text-destructive" />
                            </Button>
                            {day.open ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`day-${index}-title`}>Day Title</Label>
                              <Input
                                id={`day-${index}-title`}
                                value={day.title}
                                onChange={(e) => updateDay(index, 'title', e.target.value)}
                                placeholder="Day title (e.g., Arrival Day)"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor={`day-${index}-location`}>Location</Label>
                              <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                <Input
                                  id={`day-${index}-location`}
                                  value={day.location || ''}
                                  onChange={(e) => updateDay(index, 'location', e.target.value)}
                                  placeholder="Location for this day"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`day-${index}-description`}>Description</Label>
                            <RichTextEditor
                              value={day.description || ''}
                              onChange={(value) => updateDay(index, 'description', value)}
                              placeholder="What will you do on this day?"
                              minHeight={150}
                            />
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label>Activities & Tours</Label>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={() => addActivity(index)}
                              >
                                <Plus className="mr-1 h-3 w-3" /> Add Activity
                              </Button>
                            </div>
                            
                            {!day.activities || day.activities.length === 0 ? (
                              <div className="text-sm text-muted-foreground italic px-2">
                                No activities added yet
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {day.activities.map((activity, activityIndex) => (
                                  <div key={activityIndex} className="border rounded-md p-3 space-y-2">
                                    <div className="flex justify-between items-start">
                                      <Label htmlFor={`activity-${index}-${activityIndex}-title`}>
                                        Activity Title
                                      </Label>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeActivity(index, activityIndex)}
                                      >
                                        <Trash className="h-3 w-3 text-destructive" />
                                      </Button>
                                    </div>
                                    
                                    <Input
                                      id={`activity-${index}-${activityIndex}-title`}
                                      value={activity.title || ''}
                                      onChange={(e) => updateActivity(index, activityIndex, 'title', e.target.value)}
                                      placeholder="Visit Santa Justa Lift"
                                    />
                                    
                                    <RichTextEditor
                                      value={activity.description || ''}
                                      onChange={(value) => updateActivity(index, activityIndex, 'description', value)}
                                      placeholder="Activity description"
                                      minHeight={100}
                                    />
                                    
                                    <div className="space-y-1">
                                      <Label htmlFor={`activity-${index}-${activityIndex}-link`}>
                                        Affiliate Link (optional)
                                      </Label>
                                      <Input
                                        id={`activity-${index}-${activityIndex}-link`}
                                        value={activity.affiliate_link || ''}
                                        onChange={(e) => updateActivity(index, activityIndex, 'affiliate_link', e.target.value)}
                                        placeholder="https://booking-site.com/affiliate-link"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center">
                              <Hotel className="h-4 w-4 mr-2 text-muted-foreground" />
                              <Label>Accommodation</Label>
                            </div>
                            
                            <div className="space-y-2">
                              <Input
                                placeholder="Hotel or Accommodation Name"
                                value={day.accommodation?.name || ''}
                                onChange={(e) => updateAccommodation(index, 'name', e.target.value)}
                              />
                              
                              <div className="space-y-1">
                                <Label>Booking Link (affiliate)</Label>
                                <Input
                                  placeholder="https://booking.com/your-affiliate-link"
                                  value={day.accommodation?.booking_link || ''}
                                  onChange={(e) => updateAccommodation(index, 'booking_link', e.target.value)}
                                />
                              </div>
                              
                              <RichTextEditor
                                placeholder="Notes about the accommodation"
                                value={day.accommodation?.notes || ''}
                                onChange={(value) => updateAccommodation(index, 'notes', value)}
                                minHeight={100}
                              />
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="tags">
            <ItineraryTagsTab 
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
            />
          </TabsContent>
        </Tabs>
        
        <div className="mt-6">
          <Card className="glass-card">
            <CardFooter className="flex justify-between py-4">
              <Button variant="outline" onClick={() => navigate(`/dashboard/itineraries/${id}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> Save Changes
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
};

export default EditItinerary;
